import { cmp } from 'semver';
const logLevels = {
    ERROR: 4,
    WARN: 3,
    INFO: 2,
    DEBUG: 1,
};
/**
 * Logs a message with a specific log level and additional data.
 * @param {LogLevel} level - The log level.
 * @param {string} msg - The message to log.
 * @param {any} data - Additional data to log.
 */
export const log = (level, msg, data) => {
    if (logLevels[level]) {
        console.log(JSON.stringify({ level, msg, data }));
    }
};
/**
 * Converts body information to raw data.
 * @param {BodyInfo} body - The body information.
 * @returns {Buffer | string} - The raw body data.
 */
export const toRawBody = (body) => {
    return body.encoding === 'base64'
        ? Buffer.from(body.data, 'base64')
        : body.data;
};
/**
 * Transforms CloudFront headers to a standard HeadersInit format.
 * @param {CloudFrontHeaders} headers - The CloudFront headers.
 * @returns {HeadersInit} - The transformed headers.
 */
export const transformIncomingHeaders = (headers) => {
    return Object.fromEntries(Object.entries(headers).map(([k, vs]) => [k, vs[0].value]));
};
/**
 * Gets the headers for an S3 request.
 * @param {CloudFrontRequest} request - The CloudFront request.
 * @returns {CloudFrontHeaders} - The headers for the S3 request.
 */
export const getS3Headers = (request) => {
    return {
        host: [{ key: 'Host', value: request.origin.s3.domainName }],
        accept: request.headers.accept,
        'x-forwarded-for': request.headers['x-forwarded-for'],
        'user-agent': [{ key: 'User-Agent', value: 'Amazon CloudFront' }],
        via: request.headers.via,
        pragma: [{ key: 'Pragma', value: 'no-cache' }],
        'cache-control': [{ key: 'Cache-Control', value: 'no-cache' }],
    };
};
/**
 * Removes trailing slash or '/index.html' from the given URI.
 *
 * @param {string} uri - The URI to disambiguate.
 * @returns {string} - The disambiguated URI.
 *
 * @example
 * // Returns 'http://example.com'
 * getDisambiguatedURI('http://example.com/');
 *
 * @example
 * // Returns 'http://example.com'
 * getDisambiguatedURI('http://example.com/index.html');
 */
export const getDisambiguatedURI = (uri) => {
    return uri.endsWith('/') ? uri.slice(0, -1) : uri.replace('/index.html', '');
};
/**
 * Creates the user context based on headers and query parameters.
 * @param {CloudFrontHeaders} headers - The CloudFront headers.
 * @param {string} queryString - The query string.
 * @returns {RequestContext} - The created RequestContext object.
 */
export const getRequestContext = (headers, queryString) => {
    const inAppStatus = resolveInApp(headers['user-agent']?.[0].value || '', new URLSearchParams(queryString));
    const decodedQueryString = decodeURIComponent(queryString);
    const paramsObj = new URLSearchParams(decodedQueryString);
    const params = Object.fromEntries(paramsObj.entries());
    return {
        ...inAppStatus,
        src: params['src_tag'],
        params,
        country: headers['cloudfront-viewer-country']?.[0].value,
        region: headers['cloudfront-viewer-country-region']?.[0].value,
        city: headers['cloudfront-viewer-city']?.[0].value,
        zipCode: headers['cloudfront-viewer-postal-code']?.[0].value,
        isDesktop: headers['cloudfront-is-desktop-viewer']?.[0].value === 'true',
        isMobile: headers['cloudfront-is-mobile-viewer']?.[0].value === 'true',
        isTablet: headers['cloudfront-is-tablet-viewer']?.[0].value === 'true',
        isIOS: headers['cloudfront-is-ios-viewer']?.[0].value === 'true',
        isAndroid: headers['cloudfront-is-android-viewer']?.[0].value === 'true',
        isSmartTV: headers['cloudfront-is-smarttv-viewer']?.[0].value === 'true'
    };
};
/**
 * Resolves the in-app status based on the user agent and query parameters.
 * @param {string} userAgent - The user agent string.
 * @param {URLSearchParams} queryParams - The query parameters.
 * @returns {InAppStatus} - The resolved in-app status.
 */
export const resolveInApp = (userAgent, queryParams) => {
    const APP_NAME_REGEX = /appname:([^|]+)/i;
    const APP_VERSION_REGEX = /appversion:([0-9._]+)/;
    const APP_STORE_NAME_REGEX = /store:([^|]+)/i;
    const extractMatch = (regex, text) => {
        const matches = regex.exec(text);
        return matches ? matches[1] : null;
    };
    // TODO: clean this up later
    const CONSTANTS = {
        REGEX: {
            APP_NAME: /appname:([^|]+)/i,
            APP_VERSION: /appversion:([0-9._]+)/,
            APP_STORE_NAME: /store:([^|]+)/i
        },
        STORE_NAMES: {
            AMAZON: 'amazon app store',
            APPLE: 'apple app store',
            GOOGLE_PLAY: 'google play store',
            SAMSUNG: 'samsung app store'
        },
        PLATFORMS: {
            MAC: 'MAC',
            WINDOWS: 'WIN'
        },
        COOKIE_NAMES: {
            LINK_FROM_APP: 'link_from_app'
        },
        URL_PARAMS: {
            LINK_FROM_APP: 'linkFromApp'
        },
        MOBILE_KEYWORDS: [
            'Mobi',
            'Android',
            'iPhone',
            'iPad',
            'iPod',
            'BlackBerry',
            'IEMobile',
            'Opera Mini'
        ],
        IOS_KEYWORDS: ['iPhone', 'iPad', 'iPod']
    };
    const logoutReq = queryParams.get('logoutReq');
    const appName = extractMatch(APP_NAME_REGEX, userAgent) || queryParams.get('appname');
    const appVersion = extractMatch(APP_VERSION_REGEX, userAgent) || queryParams.get('appversion');
    const appStoreName = extractMatch(APP_STORE_NAME_REGEX, userAgent) || queryParams.get('store');
    const isSmartTvApp = Boolean(userAgent?.toLowerCase().includes('smarttv'));
    const isAndroidApp = Boolean(appStoreName?.toLowerCase().includes(CONSTANTS.STORE_NAMES.GOOGLE_PLAY));
    const isAmazonApp = appStoreName?.toLowerCase() === CONSTANTS.STORE_NAMES.AMAZON;
    const isIosApp = appStoreName?.toLowerCase() === CONSTANTS.STORE_NAMES.APPLE;
    const isSamsungApp = appStoreName?.toLowerCase() === CONSTANTS.STORE_NAMES.SAMSUNG;
    const isAppDetected = Boolean(appName || appVersion || appStoreName || logoutReq);
    return {
        isAndroidApp,
        isAmazonApp,
        isIosApp,
        isSamsungApp,
        isSmartTvApp,
        appStoreName,
        inApp: isAppDetected,
        appVersion: appVersion,
    };
};
/**
 * Resolves a condition expression based on the request context.
 * @param {string} expression - The condition expression.
 * @param {RequestContext} requestContext - The request context.
 * @returns {boolean} - The result of evaluating the condition expression.
 */
export const resolveConditionExpression = (expression, requestContext) => {
    const requestContextKeys = Object.keys(requestContext).join(', ');
    const enhancedExpression = expression.replace(/req\.appVersion\s*([<>=!]+)\s*(['"])([\d.]+)\2/g, (match, operator, quote, version) => {
        return `cmp(req.appVersion, '${operator}', '${version}')`;
    });
    const conditionsFunction = new Function('req', 'cmp', `
    const { ${requestContextKeys} } = req;
    return ${enhancedExpression};
  `);
    return conditionsFunction(requestContext, cmp);
};
// export const bodyEncodingFromMime = (mime: string | null): 'base64' | 'text' | undefined => {
//   if (!mime) return undefined;
//   mime = mime.split(';')[0];
//   if (mime.startsWith('text/')) return 'text';
//   if (mime.endsWith('+xml')) return 'text';
//   if (['application/json', 'application/xml', 'application/js', 'application/javascript'].includes(mime))
//     return 'text';
//   return 'base64';
// }
// export const transformResponse = async (rendered: Response): Promise<CloudFrontResultResponse>  => {
//   const bodyEncoding = bodyEncodingFromMime(rendered.headers.get('content-type'));
//   let body: string | undefined;
//   if (bodyEncoding === 'text') {
//     body = await rendered.text();
//   } else if (bodyEncoding === 'base64') {
//     const aBuf = await rendered.arrayBuffer();
//     const buf = Buffer.from(aBuf);
//     body = buf.toString('base64');
//   }
//   return {
//     status: rendered.status.toString(),
//     headers: transformOutgoingHeaders(rendered.headers),
//     body,
//     bodyEncoding,
//   };
// }
// export const transformOutgoingHeaders = (headers: Headers): CloudFrontHeaders => {
//   const rv: CloudFrontHeaders = {};
//   headers.forEach((v: string, k: string) => {
//     if (isBlacklisted(k.toLowerCase())) return;
//     rv[k.toLowerCase()] = [
//       {
//         key: k,
//         value: v,
//       },
//     ];
//   });
//   if (!rv['cache-control']) {
//     rv['cache-control'] = [
//       {
//         key: 'Cache-Control',
//         value: 'no-store',
//       },
//     ];
//   }
//   return rv;
// }
